// E8PentagonSquareAreaClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 13) - kwadrat i trojkat w pieciokacie.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import SvgMathLabel from "../../../../components/diagrams/SvgMathLabel";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const HEIGHTS = [4, 5, 6, 7, 8, 9, 10, 12];
const SIDES = [4, 5, 6, 7, 8, 9, 10, 12];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildOptions(correct, distractors, keepOrder = false) {
  const unique = [];
  [correct, ...distractors].forEach((value) => {
    if (value > 0 && !unique.includes(value)) unique.push(value);
  });
  let offset = 1;
  while (unique.length < 4) {
    const value = (Math.sqrt(correct) + offset) ** 2;
    if (!unique.includes(value)) unique.push(value);
    offset += 1;
  }
  const selected = [correct, ...unique.filter((value) => value !== correct)].slice(0, 4);
  const ordered = (keepOrder ? selected : shuffle(selected)).map((value) => ({ value, label: String(value) }));
  return { options: ordered, correctIndex: ordered.findIndex((option) => option.value === correct) };
}

function buildData({ triangleArea, height, options = null }) {
  const diagonal = (2 * triangleArea) / height;
  const squareArea = diagonal * diagonal;
  const builtOptions = options || buildOptions(squareArea, [diagonal * diagonal, triangleArea, height * height, squareArea / 2]);
  return { triangleArea, height, diagonal, squareArea, options: builtOptions.options, correctIndex: builtOptions.correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8PentagonSquareAreaClosedTask",
    data: buildData({
      triangleArea: 28,
      height: 7,
      options: { options: [16, 32, 49, 64].map((value) => ({ value, label: String(value) })), correctIndex: 3 },
    }),
    source: { variant: "original", exam: "E8", session: "2026_MAJ", taskNumber: 13 },
    tags: ["e8", "planimetria", "kwadrat", "trojkat", "pole"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const side = pick(SIDES);
  const height = pick(HEIGHTS);
  const rationalArea = (side * height) / 2;
  return {
    type: "E8PentagonSquareAreaClosedTask",
    data: buildData({ triangleArea: rationalArea, height }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_13" },
    tags: ["e8", "planimetria", "kwadrat", "trojkat", "pole"],
  };
}

function Diagram() {
  const fill = "rgba(255,159,10,0.18)";
  const accent = "#ff9f0a";
  const A = { x: 98, y: 112 };
  const B = { x: 98, y: 184 };
  const C = { x: 170, y: 184 };
  const D = { x: 170, y: 112 };
  const E = { x: 134, y: 34 };
  const labels = [["A", A, -16, 6], ["B", B, -16, 16], ["C", C, 16, 16], ["D", D, 16, 6], ["E", E, 0, -10]];
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "14px 0" }}>
      <svg width="270" height="210" viewBox="0 0 270 210" role="img" aria-label="Pięciokąt ABCDE z kwadratem ABCD i trójkątem ADE">
        <polygon points={`${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y} ${D.x},${D.y}`} fill={fill} stroke="currentColor" strokeWidth="2.5" />
        <polygon points={`${A.x},${A.y} ${D.x},${D.y} ${E.x},${E.y}`} fill="rgba(255,159,10,0.28)" stroke="currentColor" strokeWidth="2.5" />
        <line x1={A.x} y1={A.y} x2={D.x} y2={D.y} stroke={accent} strokeWidth="2" strokeDasharray="7 4" />
        {labels.map(([label, p, dx, dy]) => (
          <SvgMathLabel key={label} x={p.x + dx} y={p.y + dy} math={label} width={22} height={22} fontSize={15} />
        ))}
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Przekątna <InlineMath math="AD" /> dzieli pięciokąt <InlineMath math="ABCDE" /> na trójkąt <InlineMath math="ADE" /> i na kwadrat <InlineMath math="ABCD" />. Pole trójkąta <InlineMath math="ADE" /> jest równe <InlineMath math={`${data.triangleArea}`} />, a wysokość poprowadzona z wierzchołka <InlineMath math="E" /> na bok <InlineMath math="AD" /> jest równa <InlineMath math={`${data.height}`} />.
      </p>
      <Diagram />
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>Pole kwadratu <InlineMath math="ABCD" /> jest równe</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => <div key={option.value}><ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}`} /></div>)}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return <ABCDInput options={data.options.map((option, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))} value={userAnswer} onChange={onChange} disabled={disabled} />;
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.squareArea}`} />;
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Korzystamy ze wzoru na pole trójkąta.</p>
      <BlockMath math={`${data.triangleArea}=\\frac{AD\\cdot ${data.height}}{2}`} />
      <BlockMath math={`AD=\\frac{2\\cdot ${data.triangleArea}}{${data.height}}=${data.diagonal}`} />
      <p><strong>Krok 2.</strong> Odcinek <InlineMath math="AD" /> jest bokiem kwadratu <InlineMath math="ABCD" />.</p>
      <BlockMath math={`P=AD^2=${data.diagonal}^2=${data.squareArea}`} />
      <p>Poprawna odpowiedź to <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.</p>
    </div>
  );
}



